import { ERROR_TYPE } from '@/features/game-window-integration/helpers/error-mapping';
import { handleUnknownMapping } from '@/features/game-window-integration/helpers/generic-helpers';
import { ProcessedError } from '@/features/game-window-integration/providers/generic-provider/payload-adaptors/contracts';
import { GenericErrorEvent } from '@/features/game-window-integration/providers/generic-provider/types';

/**
 * Shared implementation used by every provider adaptor's `processError`.
 * It is purely driven by the provider's `errorCodesRemap` (code → internal
 * ERROR_TYPE) and `errorMessages` (code → human-readable fallback), so
 * provider-specific behaviour is expressed entirely through those maps.
 */
export const runProcessError = (
  error: GenericErrorEvent | undefined,
  errorCodesRemap: Record<string | number, string>,
  errorMessages: Record<string | number, string>,
): ProcessedError | null => {
  if (!error) return null;
  const primaryErrorCode =
    error.name ?? error.code ?? error.errorCode ?? error.type;
  let errorCode = error.internalErrorCode;
  if (errorCodesRemap[primaryErrorCode]) errorCode = primaryErrorCode;

  const errorType =
    (errorCode && errorCodesRemap[errorCode]) ?? ERROR_TYPE.GENERIC;

  const resolveErrorMessage = (): string => {
    if (
      !errorCodesRemap[primaryErrorCode] &&
      error.internalErrorCode &&
      errorMessages[error.internalErrorCode]
    ) {
      return errorMessages[error.internalErrorCode];
    }

    if (error.errorMessage && errorMessages[error.errorMessage]) {
      return errorMessages[error.errorMessage];
    }

    return error.message ?? error.description ?? error.errorMessage ?? '';
  };

  const errorMessage = resolveErrorMessage();
  const resolvedErrorCode =
    error.code ?? error.internalErrorCode ?? error.errorCode;

  const isMapped = !!(errorCode !== undefined && errorCodesRemap[errorCode]);
  if (!isMapped && error) {
    handleUnknownMapping(error, errorCode ?? 'UNKNOWN');
  }

  if (!errorType) return null;
  return {
    errorType,
    errorMessage,
    errorCode: resolvedErrorCode,
    errorLevel: error.level,
  };
};
